import { type Dispatch } from '@reduxjs/toolkit';
import { OTP_RESEND_STATE, ROUTE_NAMES, SESSION_PHONE_NUMBER } from '../../../constants';
import { type RegistrationStepType } from 'interfaces';
import { getStorageItem, removeStorageItem, setStorageItem } from 'utils';
import { updateUserAction } from 'store/slices/user';
import { shouldRemoveSessionPhone } from './checks';
import { AUTH_FLOWS, REG_BLOCK_REASONS } from './constants';
import { parseSessionPhoneRaw, parseSessionPhoneStorage } from './sessionPhoneStorage';
import { clearLoginOtpSessionTtl } from './loginSessionTtl';

interface OtpResendState {
  phone: string;
  count: number;
}

export const getOtpResendCount = (phone: string): number => {
  const raw = getStorageItem(OTP_RESEND_STATE);

  if (!raw) {
    return 0;
  }

  try {
    const parsed = JSON.parse(raw) as OtpResendState;

    return parsed.phone === phone ? parsed.count : 0;
  } catch {
    return 0;
  }
};

export const saveOtpResendCount = (phone: string, count: number): void => {
  setStorageItem(OTP_RESEND_STATE, JSON.stringify({ phone, count } satisfies OtpResendState));
};

/** Синхронизирует `auth_flow` из `s_phn`; возвращает `new` или `null`, если сессии нет. */
export const syncAuthFlowFromSession = (dispatch: Dispatch): boolean | null => {
  const session = parseSessionPhoneStorage();

  if (!session?.value && !session?.valuemx) {
    return null;
  }

  dispatch(
    updateUserAction({
      user: { auth_flow: session.new ? AUTH_FLOWS.REG : AUTH_FLOWS.LOGIN },
    }),
  );

  return Boolean(session.new);
};

export const handleRegType = (
  stepData: RegistrationStepType,
  regTypeGa: string,
  dispatch: Dispatch,
  setRegTypeItem?: (type: any) => void,
) => {
  const isPA = stepData.step_name === ROUTE_NAMES.PersonalAccount;

  if (!isPA) {
    setRegTypeItem?.(regTypeGa);
    dispatch(updateUserAction({ user: { registration_type: regTypeGa } }));
  }

  if (shouldRemoveSessionPhone(stepData?.step_name, isPA)) {
    removeStorageItem(SESSION_PHONE_NUMBER);
  }
};

export const handlePhoneNumberUpdate = (stepData: RegistrationStepType, callback?: (arg?: string) => void) => {
  if (stepData.registration_block_reason === REG_BLOCK_REASONS.NEED_LOGIN_ORIGIN_ACC) {
    const maskedNumber = stepData.duplicate?.origin_info?.last_phone_digits;

    if (!maskedNumber) {
      return;
    }

    const parsedPhone = parseSessionPhoneRaw(getStorageItem(SESSION_PHONE_NUMBER) ?? '');
    setStorageItem(
      SESSION_PHONE_NUMBER,
      JSON.stringify({
        value: parsedPhone.value,
        valuemx: maskedNumber,
        ...(parsedPhone.new ? { new: true } : {}),
        ...(parsedPhone.sender ? { sender: parsedPhone.sender } : {}),
        ...(parsedPhone.initial_sender ? { initial_sender: parsedPhone.initial_sender } : {}),
      }),
    );
    clearLoginOtpSessionTtl();
    callback?.(maskedNumber);
  }
};

export const getBlockReasonNavigation = (stepData: RegistrationStepType): string | null => {
  if (stepData.registration_block_reason === REG_BLOCK_REASONS.NEED_MANUAL_VERIFICATION) {
    return ROUTE_NAMES.Selfie;
  }
  return null;
};
